import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RiAddLine, RiArrowLeftLine, RiArrowRightLine } from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Banner } from '@/design-system/banner';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import { StepIndicator } from '@/components';
import { useDriversDropdown } from '@/pages/scheduled-instant/components/useDriversDropdown';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import {
  ujpAPI,
  type CreateUjpResult,
  type UjpClientRule,
  type UjpDetail,
  type UjpSubconVendor,
  type UjpVehicle,
} from '@/services/api/ujp';
import { routePlansAPI, type RoutePlan } from '@/services/api/routePlans';
import {
  tariffsAPI,
  type TariffConfig,
  type TariffRing,
} from '@/services/api/tariffs';

import { UJP_COPY } from '../copy';
import { formatRupiah } from '../format';
import {
  autoReverse,
  blankUjpForm,
  formFromDetail,
  toCreatePayload,
  toEstimatePayload,
  validateAll,
  incompleteSteps,
  stepKindsFor,
  validateStepKind,
  wizardStepsFor,
  type UjpErrors,
  type UjpFormState,
  type UjpStepIndex,
} from './ujpForm';
import {
  UjpCostStep,
  UjpDriverStep,
  UjpInfoStep,
  UjpReviewStep,
  UjpRouteStep,
} from './UjpWizardSteps';
import { usesQris } from './UjpTiles';
import { useUjpEstimate } from './useUjpEstimate';
import { suggestRing } from './ringSuggest';

/**
 * CreateUjpModal — the 5-step, forward-only UJP wizard (DD2) in a `FormModal`
 * 2xl. The modal owns the whole form, the master lookups and the live server
 * estimate; the step bodies (`UjpWizardSteps`) are pure controlled views.
 *
 * Money math never happens here (assessment D5): every number shown comes from
 * `POST /v1/ujp/estimate`, and "Ajukan UJP" is blocked until a FRESH estimate
 * for the current inputs has landed, so what ops approves is what finance sees.
 */


export interface CreateUjpModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Edit an existing UJP in place (CR-3). SUBMITTED → save the change;
   * REJECTED → fix and resubmit. When null the wizard creates a new UJP.
   */
  editing?: UjpDetail | null;
  /** Created — the list refetches and opens the new row. */
  onCreated?: (result: CreateUjpResult) => void;
  /** Edited / resubmitted — the panel + list refetch. */
  onUpdated?: () => void;
  /** Tests turn the server legs call off inside the drawer. */
  autoLegs?: boolean;
}

const CreateUjpModal: React.FC<CreateUjpModalProps> = ({
  open,
  onClose,
  editing,
  onCreated,
  onUpdated,
  autoLegs = true,
}) => {
  const w = UJP_COPY.wizard;

  const [step, setStep] = useState<UjpStepIndex>(1);
  const [form, setForm] = useState<UjpFormState>(() => blankUjpForm());
  const [errors, setErrors] = useState<UjpErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const clients = useProvidersDropdown(open);
  // Both payees pick a real driver now (subcon rides on the shared driver), so
  // the directory loads whenever the wizard is open.
  const riders = useDriversDropdown(open, { type: 'DRIVER' });

  // Payee-conditional step model (CR-D1): subcon skips the Driver step — its
  // rider + rekening live in Biaya — so it has 4 steps to the driver's 5.
  const stepKinds = stepKindsFor(form.payee);
  const stepCount = stepKinds.length;
  const currentKind = stepKinds[step - 1] ?? 'info';
  const steps = wizardStepsFor(form.payee);

  // ── Reset / prefill on open ────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setErrors({});
    setSubmitting(false);
    setForm(editing ? formFromDetail(editing) : blankUjpForm());
  }, [open, editing]);

  const patch = useCallback((next: Partial<UjpFormState>) => {
    setForm((prev) => ({ ...prev, ...next }));
  }, []);

  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // A driver→subcon switch drops the Driver step; keep `step` inside the shorter
  // list (the toggle lives on Biaya, so this is a defensive clamp).
  useEffect(() => {
    if (step > stepCount) setStep(stepCount as UjpStepIndex);
  }, [step, stepCount]);

  // ── Client rule (CR-D7) ────────────────────────────────────────────────────
  const [rule, setRule] = useState<UjpClientRule | null>(null);
  const [ruleLoading, setRuleLoading] = useState(false);

  useEffect(() => {
    if (!open || !form.clientId) {
      setRule(null);
      return;
    }
    let cancelled = false;
    setRuleLoading(true);
    ujpAPI
      .listClientConfigs()
      .then((rows) => {
        if (cancelled) return;
        const row = rows.find((r) => String(r.clientId) === form.clientId);
        // An absent client means the defaults: pool legs not charged, no
        // reverse charge (Client Configs - List).
        setRule({
          chargedPositioning: row?.chargedPositioning ?? false,
          reverseCharge: row?.reverseCharge ?? '0',
        });
        // E-money no longer defaults from the client rule — it now comes from
        // the SELECTED vehicle's `defaultEMoney` (see `applyVehicle`).
      })
      .catch(() => {
        if (!cancelled) setRule(null);
      })
      .finally(() => {
        if (!cancelled) setRuleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, form.clientId, editing]);

  // Pre-tick "Perjalanan reverse" once the route shows a RETURN leg (CR-D8).
  useEffect(() => {
    const next = autoReverse(form, rule);
    if (next !== form.isReverse) setForm((prev) => ({ ...prev, isReverse: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.route, rule, form.reverseTouched]);

  // ── Masters ────────────────────────────────────────────────────────────────
  // Route plans for the client (CR-2) — the Rute step picks one or opens the
  // Route Planner drawer to make a new one.
  const [plans, setPlans] = useState<RoutePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [routeQuery, setRouteQuery] = useState('');

  useEffect(() => {
    if (!open || !form.clientId) {
      setPlans([]);
      return;
    }
    let cancelled = false;
    setPlansLoading(true);
    setPlansError(null);
    routePlansAPI
      .list({
        clientId: Number(form.clientId),
        search: routeQuery || undefined,
        active: true,
      })
      .then((rows) => !cancelled && setPlans(rows))
      .catch((err: unknown) => {
        if (cancelled) return;
        setPlans([]);
        setPlansError(err instanceof Error ? err.message : 'error');
      })
      .finally(() => !cancelled && setPlansLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, form.clientId, routeQuery]);

  const handlePlanCreated = useCallback((plan: RoutePlan) => {
    setPlans((prev) => [plan, ...prev.filter((p) => p.id !== plan.id)]);
  }, []);

  const [vehicles, setVehicles] = useState<UjpVehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehicleQuery, setVehicleQuery] = useState('');

  useEffect(() => {
    // Both payees show the vehicle picker in Biaya now (subcon creates a real
    // shipment), so vehicles load whenever the wizard is open.
    if (!open) return;
    let cancelled = false;
    setVehiclesLoading(true);
    ujpAPI
      .listVehicles(vehicleQuery || undefined)
      .then((rows) => !cancelled && setVehicles(rows))
      .catch(() => !cancelled && setVehicles([]))
      .finally(() => !cancelled && setVehiclesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, form.payee, vehicleQuery]);

  const [vendors, setVendors] = useState<UjpSubconVendor[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorQuery, setVendorQuery] = useState('');

  useEffect(() => {
    if (!open || form.payee !== 'subcon') return;
    let cancelled = false;
    setVendorsLoading(true);
    ujpAPI
      .listSubconVendors(vendorQuery || undefined)
      .then((rows) => !cancelled && setVendors(rows))
      .catch(() => !cancelled && setVendors([]))
      .finally(() => !cancelled && setVendorsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, form.payee, vendorQuery]);

  /** The dated energy price for the vehicle's fuel type (CR-D5). */
  const [masterPrice, setMasterPrice] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !form.fuelType || !form.deliveryDate) {
      setMasterPrice(null);
      return;
    }
    let cancelled = false;
    ujpAPI
      // Region-aware (R3): the regional price wins over the global one.
      .listEnergyPrices(form.deliveryDate, form.region || null)
      .then((rows) => {
        if (cancelled) return;
        const row = rows.find((r) => r.fuelType === form.fuelType);
        setMasterPrice(row?.price ?? null);
      })
      .catch(() => !cancelled && setMasterPrice(null));
    return () => {
      cancelled = true;
    };
  }, [open, form.fuelType, form.deliveryDate, form.region]);

  // ── Tariff / ring (CR-4) ─────────────────────────────────────────────────────
  // Resolve the client's tariff for the delivery date, tag the UJP with its id,
  // and load rings for a PER_RING client so the wizard can suggest one.
  const [tariff, setTariff] = useState<TariffConfig | null>(null);
  const [rings, setRings] = useState<TariffRing[]>([]);
  const [ringsLoading, setRingsLoading] = useState(false);

  useEffect(() => {
    if (!open || !form.clientId || !form.deliveryDate) {
      setTariff(null);
      setRings([]);
      return;
    }
    let cancelled = false;
    setRingsLoading(true);
    tariffsAPI
      .list({ clientId: Number(form.clientId), activeOn: form.deliveryDate })
      .then(async (rows) => {
        if (cancelled) return;
        const cfg = rows.find((r) => r.active) ?? rows[0] ?? null;
        setTariff(cfg);
        // Tag the UJP with the resolved config unless it already carries one.
        if (cfg?.id) {
          setForm((prev) =>
            prev.tariffConfigId === cfg.id
              ? prev
              : { ...prev, tariffConfigId: cfg.id }
          );
        }
        if (cfg?.tipe === 'PER_RING') {
          const loaded = cfg.rings ?? (await tariffsAPI.listRings(cfg.id));
          // Inactive rings (TODO-22) stay on the config for old snapshots but
          // are never offered to a new UJP — picker and auto-suggest alike.
          if (!cancelled) setRings(loaded.filter((r) => r.active !== false));
        } else if (!cancelled) {
          setRings([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTariff(null);
          setRings([]);
        }
      })
      .finally(() => !cancelled && setRingsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, form.clientId, form.deliveryDate]);

  // Auto-suggest a ring from the last drop until ops picks one (CR4-D5).
  useEffect(() => {
    if (form.ringTouched || rings.length === 0) return;
    // Billable drops only — a pool drop is the truck going home, not a destination.
    const drops = form.route.stops.filter((s) => s.type === 'DROP_OFF' && !s.pool);
    const last = drops[drops.length - 1];
    // Name + address: the keyword matcher (CR4b-D12) keys on city names, which
    // usually live in the address rather than the stop label.
    const dest = [last?.name, last?.address].filter(Boolean).join(' ');
    const suggested = suggestRing(rings, dest);
    if (suggested && suggested !== form.ringId) {
      setForm((prev) => ({ ...prev, ringId: suggested }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rings, form.route, form.ringTouched]);

  // ── Estimate ───────────────────────────────────────────────────────────────
  const estimatePayload = useMemo(() => toEstimatePayload(form), [form]);
  const estimate = useUjpEstimate(estimatePayload, { enabled: open });

  // ── Navigation ─────────────────────────────────────────────────────────────
  const bodyRef = useRef<HTMLDivElement>(null);

  const goNext = () => {
    const found = validateStepKind(currentKind, form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      // Focus the first invalid control so the fix is one keystroke away.
      window.requestAnimationFrame(() => {
        const el = bodyRef.current?.querySelector<HTMLElement>(
          '[data-invalid="true"], [aria-invalid="true"]'
        );
        el?.scrollIntoView({ block: 'center' });
        el?.focus?.();
      });
      return;
    }
    setErrors({});
    const next = Math.min(stepCount, step + 1) as UjpStepIndex;
    setStep(next);
    // The Review step re-checks with the server before enabling Ajukan.
    if (next === stepCount) estimate.refresh();
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1) as UjpStepIndex);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const allErrors = useMemo(() => validateAll(form), [form]);
  const missing = useMemo(
    () => incompleteSteps(allErrors, form.payee),
    [allErrors, form.payee]
  );
  const canSubmit =
    step === stepCount &&
    missing.length === 0 &&
    estimate.fresh &&
    !submitting;

  const handleSubmit = async () => {
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      return;
    }
    if (!estimate.fresh) {
      toast.error(UJP_COPY.validation.estimateFresh);
      return;
    }
    setSubmitting(true);
    try {
      const payload = toCreatePayload(form);
      if (editing) {
        await ujpAPI.update(editing.id, payload);
        toast.success(
          editing.status === 'REJECTED'
            ? UJP_COPY.toast.resubmitted(editing.referenceId)
            : UJP_COPY.toast.updated(editing.referenceId)
        );
        onUpdated?.();
      } else {
        const result = await ujpAPI.create(payload);
        onCreated?.(result);
      }
      onClose();
    } catch (err) {
      toast.error(UJP_COPY.toast.submitFailed, {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Footer ─────────────────────────────────────────────────────────────────
  const resubmitting = editing?.status === 'REJECTED';
  const submitLabel = editing
    ? resubmitting
      ? w.resubmit
      : w.saveChanges
    : w.submit;
  const submittingLabel = editing
    ? resubmitting
      ? w.resubmitting
      : w.savingChanges
    : w.submitting;

  /** The strip rides on Rute and Driver only — Biaya and Review carry their own. */
  const showStrip = currentKind === 'route' || currentKind === 'driver';

  const strip = (
    <div
      className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
      aria-live="polite"
    >
      <span className="label-eyebrow">{UJP_COPY.estimate.total}</span>
      <span
        className={`font-mono text-[15px] font-bold tabular-nums ${
          estimate.fresh ? 'text-text-strong-950' : 'text-text-soft-400'
        }`}
      >
        {estimate.result ? formatRupiah(estimate.result.estimatedAmount) : '—'}
      </span>
      {estimate.status === 'error' ? (
        <>
          <Badge status="error" size="sm">
            {UJP_COPY.estimate.failed}
          </Badge>
          <Button tone="neutral" style="ghost" size="xs" onClick={estimate.refresh}>
            {UJP_COPY.estimate.retry}
          </Button>
        </>
      ) : estimate.result && !estimate.fresh ? (
        <Badge status="warning" size="sm">
          {UJP_COPY.estimate.stale}
        </Badge>
      ) : estimate.result ? (
        <span className="flex flex-wrap items-center gap-1">
          <Badge status="success" size="sm">
            {`${UJP_COPY.estimate.flazz} ${formatRupiah(estimate.result.totalUangJalanFlazz)}`}
          </Badge>
          {usesQris(form.eMoney) && (
            <Badge status="information" size="sm">
              {`${UJP_COPY.estimate.qrisShort} ${formatRupiah(estimate.result.totalUangJalanQris)}`}
            </Badge>
          )}
          <Badge status="feature" size="sm">
            {`${UJP_COPY.estimate.transfer} ${formatRupiah(estimate.result.totalUangJalanTransfer)}`}
          </Badge>
        </span>
      ) : null}
    </div>
  );

  const footer = (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {step > 1 && (
          <Button
            tone="neutral"
            style="ghost"
            onClick={goBack}
            disabled={submitting}
            className="max-sm:w-full"
          >
            <RiArrowLeftLine />
            {w.back}
          </Button>
        )}
        {showStrip && strip}
      </div>
      <div className="flex items-center justify-end gap-2 max-sm:flex-col-reverse max-sm:items-stretch">
        <Button
          tone="neutral"
          style="stroke"
          onClick={onClose}
          disabled={submitting}
        >
          {w.cancel}
        </Button>
        {step < stepCount ? (
          <Button onClick={goNext}>
            {w.next}
            <RiArrowRightLine />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
            {submitting ? submittingLabel : submitLabel}
          </Button>
        )}
      </div>
    </div>
  );

  const clientName = form.clientId ? clients.labelFor(form.clientId) : '';

  const stepProps = { form, patch, errors, clearError };

  return (
    <FormModal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      icon={<RiAddLine />}
      title={editing ? w.editTitle(editing.referenceId) : w.title}
      subtitle={w.subtitle}
      size="2xl"
      headerExtra={
        <span
          className="font-mono text-[11px] tabular-nums text-text-soft-400"
          title={w.pendingRefHint}
        >
          {w.pendingRef}
        </span>
      }
      footer={footer}
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      contentRef={bodyRef}
    >
      {/*
        Enter must never advance or submit (DD12): the body is a plain div, not
        a <form>, and a stray Enter inside an input is swallowed here.
      */}
      <div
        className="space-y-4"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT')
            e.preventDefault();
        }}
      >
        <div className="pb-1">
          <StepIndicator
            steps={steps}
            currentStep={step}
            className="hidden sm:flex"
          />
          <StepIndicator
            steps={steps}
            currentStep={step}
            compact
            compactLabel={(n, total, label) => w.stepOf(n, total, label)}
            className="sm:hidden"
          />
        </div>

        {currentKind === 'info' && editing?.status === 'REJECTED' && editing.rejection && (
          <Banner
            status="error"
            title={w.rejectionBanner(
              editing.rejection.reasonLabel ?? editing.rejection.reasonCode
            )}
          >
            {editing.rejection.note || w.rejectionBannerBody}
          </Banner>
        )}
        {currentKind === 'info' && (
          <UjpInfoStep
            {...stepProps}
            clients={clients}
            rule={rule}
            ruleLoading={ruleLoading}
            tariffTipe={tariff?.tipe ?? null}
            rings={rings}
            ringsLoading={ringsLoading}
          />
        )}
        {currentKind === 'route' && (
          <UjpRouteStep
            {...stepProps}
            rule={rule}
            plans={plans}
            plansLoading={plansLoading}
            plansError={plansError}
            clientName={clientName}
            onSearchPlans={setRouteQuery}
            onPlanCreated={handlePlanCreated}
            enableLegs={autoLegs}
            estimate={estimate.result}
          />
        )}
        {currentKind === 'cost' && (
          <UjpCostStep
            {...stepProps}
            vehicles={vehicles}
            vehiclesLoading={vehiclesLoading}
            onSearchVehicles={setVehicleQuery}
            vendors={vendors}
            vendorsLoading={vendorsLoading}
            onSearchVendors={setVendorQuery}
            masterPrice={masterPrice}
            estimate={estimate.result}
            estimateStatus={estimate.status}
            estimateError={estimate.error}
            onRetryEstimate={estimate.refresh}
          />
        )}
        {currentKind === 'driver' && (
          <UjpDriverStep {...stepProps} riders={riders} />
        )}
        {currentKind === 'review' && (
          <UjpReviewStep
            form={form}
            rule={rule}
            clientName={clientName}
            estimate={estimate.result}
            estimateStatus={estimate.status}
            estimateError={estimate.error}
            onRetryEstimate={estimate.refresh}
            onEdit={(s) => setStep(s)}
            missing={missing}
          />
        )}
      </div>
    </FormModal>
  );
};

export default CreateUjpModal;

/** Re-exported so the page can build the "rute disimpan" half of the toast. */
